import { Injectable, TemplateRef, inject } from '@angular/core';
import { Validators } from '@angular/forms';

import { filter, map, of, startWith } from 'rxjs';

import {
  CamAbstractInput,
  InputChoices,
  InputDropdown,
  InputLabel,
  InputPanel,
  InputTextBox,
} from '@camelot/form-model';
import { CamUsersService } from '@camelot/user';
import { extractEnum, fullName, isNonNullable } from '@camelot/utils';

import { getTypeTranslationKey } from '../helpers/type';
import { CamGridData } from '../models/grid-data';
import { UpdateViewSettingInput } from './dto/post/view';
import { FielOptions, View, ViewType } from './dto/view';

@Injectable({
  providedIn: 'root',
})
export class CamGridFormService<T extends { id: string }> {
  private _usersService = inject(CamUsersService);

  public getFiltersForm(model: CamGridData<T>): CamAbstractInput[] {
    const keys = Object.keys(model.cols);
    if (!keys || keys.length === 0) {
      return [];
    }

    return [
      new InputPanel({
        children: keys
          .filter(key => model.cols[key].enabledInFilter)
          .map(key => model.cols[key].getInputForm())
          .filter(isNonNullable),
        class: 'p-5',
        key: 'main-panel',
      }),
    ];
  }

  public getHighlightFiltersForm(model: CamGridData<T>): CamAbstractInput[] {
    const keys = Object.keys(model.cols);
    if (!keys || keys.length === 0) {
      return [];
    }

    const children = keys
      .filter(key => model.cols[key].enabledInHighlight)
      .map(key => {
        const input = model.cols[key].getInputForm();
        if (!input) {
          return null;
        }

        const label = input.label;
        input.label = '';

        return new InputPanel({
          children: [new InputLabel({ color: 'brand', key: `${input.key}-label`, label, size: 'sm' }), input],
          class: 'one-fourth',
          key: `${input.key}-wrapper`,
        });
      })
      .filter(isNonNullable);

    if (children.length === 0) {
      return [];
    }

    return [
      new InputPanel({
        children,
        contentClass: 'grid g-space-sm',
        key: 'highlight-panel',
      }),
    ];
  }

  public getViewForm(view: View | null, choiceTemplate: TemplateRef<any>): CamAbstractInput[] {
    const typeDropdown = new InputDropdown({
      class: 'pb-2',
      key: 'type',
      label: 'grid.settings.view.type',
      options: of(
        extractEnum(ViewType)
          .map(item => ({
            id: item.value.toString(),
            name: getTypeTranslationKey(item.value),
          }))
          .filter(item => item.id !== '0')
      ),
      validators: [Validators.required],
      value: view?.type.toString(),
    });
    return [
      new InputPanel({
        children: [
          new InputTextBox({
            class: 'pb-2',
            key: 'name',
            label: 'grid.settings.view.name',
            validators: [Validators.required],
            value: view?.name,
          }),
        ],
        class: 'p-5',
        key: 'main-panel',
      }),
      typeDropdown,
      new InputChoices({
        choiceTemplate: { one: choiceTemplate },
        key: 'sharedWithUserIds',
        label: 'grid.teams.form.users.label',
        multiple: true,
        options: this._usersService.fetchUsers$().pipe(
          filter(isNonNullable),
          map(users =>
            users.map(user => ({
              data: user,
              id: user.id,
              name: fullName({
                firstName: user.firstName,
                name: user.lastName,
              }),
            }))
          )
        ),
        // changeValue$ n'émet qu'au changement : sans valeur initiale l'input reste activé mais jamais rendu.
        visible$: typeDropdown.changeValue$.pipe(
          startWith(typeDropdown.value),
          map(type => Number(type) === ViewType.shared)
        ),
        withSearch: true,
      }),
    ];
  }

  public formatView(
    data: any,
    viewname: string,
    filterModel: string,
    selectedFields: string[],
    fieldOptions: FielOptions[]
  ): UpdateViewSettingInput {
    return {
      fieldOptions,
      filterModel,
      id: data['id'],
      name: data['name'],
      selectedFields,
      sharedWithUserIds: data['sharedWithUserIds'],
      type: Number(data['type']),
      viewName: viewname,
    };
  }
}
